/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  OnChanges,
  Optional,
  ViewChild,
  inject,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
  ActiveCartFacade,
  CartItemComponentOptions,
  CartOutlets,
  CartUiEventAddToCart,
} from '@spartacus/cart/base/root';
import {
  CmsAddToCartComponent,
  EventService,
  FeatureConfigService,
  Product,
  isNotNullable,
  useFeatureStyles,
} from '@spartacus/core';
import {
  CmsComponentData,
  CurrentProductService,
  ICON_TYPE,
  ProductListItemContext,
} from '@spartacus/storefront';
import { Observable, Subscription } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { GonvarriActiveCartService } from '../../core/services/gonvarri-active-cart.service';
import { isAsturias } from 'src/app/utils/check-environment';
import { calcKgEach } from 'src/app/utils/weight-utils';
import {GonvarriProduct} from "../../core/models/gonvarri-product.model";

@Component({
    selector: 'cx-add-to-cart',
    templateUrl: './gonvarri-add-to-cart.component.html',
    styleUrl: './gonvarri-add-to-cart.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GonvarriAddToCartComponent implements OnInit, OnChanges {

  isAsturias = isAsturias();
  @Input() productCode: string = "";
  @Input() showQuantity = true;
  @Input() showAddToCart = false;
  @Input() options: CartItemComponentOptions = {} as CartItemComponentOptions;
  @Input() pickupStore: string | undefined;
  @Input() numberOfSheets: number = 10;
  @Input() packageWeight: number = 1000;
  @Input() length: any | undefined = undefined;
  @Input() width: any | undefined = undefined;
  @Input() thickness: any | undefined = undefined;
  @Input() isContract: boolean | undefined;
  @Input() inventoryDisplay: boolean | undefined = true;
  @Input() isDisabled: boolean | undefined = false;
  @Input() customQuantity: number | undefined = undefined;
  @Input() hideTexts: boolean = false;
  @Input() packProductRowMaxStockQuantity: number | undefined = undefined;
  @Input() packProductFixedWeight: number | undefined = undefined;

  @Input() product: GonvarriProduct = {} as GonvarriProduct;

  @ViewChild('addToCartDialogTriggerEl') addToCartDialogTriggerEl: ElementRef = {} as ElementRef;

  isDisabledByQuantityOverStock: boolean = false;

  maxQuantity: number = 0;

  hasStock: boolean = false;
  forceInStock: boolean = false;
  inventoryThreshold: boolean = false;

  quantity = 1;

  subscription: Subscription = {} as Subscription;

  addToCartForm = new UntypedFormGroup({
  quantity: new UntypedFormControl(1, { updateOn: 'blur' }),
    });

    readonly CartOutlets = CartOutlets;

    pickupOptionCompRef: any;

    iconTypes = ICON_TYPE;

    private featureConfigService = inject(FeatureConfigService);

    /**
     * We disable the dialog launch on quantity input,
     * as it causes an unexpected change of context.
     * The expectation is only for the quantity to get updated in the Qty field.
     */
    @HostListener('document:keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
      if (!this.featureConfigService?.isEnabled('a11yQuantityOrderTabbing')) {
        return;
      }
      const eventTarget = event.target as HTMLElement;
      const isQuantityInput =
        eventTarget.ariaLabel === 'Quantity' && eventTarget.tagName === 'INPUT';
      if (event.key === 'Enter' && isQuantityInput) {
        event.preventDefault();
      }
    }

    constructor(
      protected currentProductService: CurrentProductService,
      protected cd: ChangeDetectorRef,
      protected activeCartService: GonvarriActiveCartService,
      protected eventService: EventService,
      @Optional() protected productListItemContext?: ProductListItemContext
    ) {
      useFeatureStyles('a11yQTY2Quantity');
    }

    passThrought(value: any): any {
      return value;
    }

    ngOnChanges(): void {
      this.updateComponent();
      // If there is a customQuantity, it means that this add to cart component is being called by quick stock modal component
      // and the quantity form control from the addToCartForm is not being used, so we pass the customQuantity coming from quick stock modal
      this.onQuantityChange(this.customQuantity ? this.customQuantity : this.addToCartForm.get('quantity')?.value);
    }

    ngOnInit() {
      this.updateComponent();
      this.addToCartForm.controls['quantity'].setValue(1);
      this.addToCartForm.controls['quantity'].valueChanges.subscribe(newQuantity => {
        this.onQuantityChange(newQuantity);
      });
    }

    onQuantityChange(quantity: number) {
      // Here we do 5 checks
      // 1) If we are in Asturias, the stock is by packages, so the quantity is directly compared to the maxQuantity

      // 2) If not in Asturias, if the values are considering package weight,
      // the quantity defined by the user is multiplied by the package weight also defined by the user and compared to the maxQuantity

      // 3) If not in Asturias, if the values are considering sheets, we calculate the total Kg of the sheets (number of sheets defined by the user),
      // multiplies by the quantity and compare to the maxQuantity

      // 4) This check is for PDP (If not in Asturias) when the user selects a product that is sold by fixed packageWeight, in this case the packProductFixedWeight
      // is defined and we multiply this fixed weight by the quantity and compare to the maxQuantity

      // 5) This check is for quick stock page (If not in Asturias) when the user selects a product that is sold by fixed packageWeight, in this case the maximum value permmited is sent as
      // packProductRowMaxStockQuantity and we compare the quantity to this value
      if (
        isAsturias() && quantity > this.maxQuantity
        ||
        !isAsturias() && this.packageWeight * quantity > this.maxQuantity
        ||
        !isAsturias() && (this.calcKgEach() * quantity > this.maxQuantity) && this.product.categories?.find(category => category.code?.includes("productType_sheet"))
        ||
        !isAsturias() && this.packProductFixedWeight && this.packProductFixedWeight * quantity > this.maxQuantity
        ||
        !isAsturias() && this.packProductRowMaxStockQuantity && quantity > this.packProductRowMaxStockQuantity
      ) {
        this.isDisabledByQuantityOverStock = true;
      } else {
        this.isDisabledByQuantityOverStock = false;
      }
    }

    // This method is used whey buying by sheets. It calculates the weight of each sheet based on the product's width, thickness and length
    calcKgEach(): number {
      // If the @Input product is coming from quick stock modal component instead of PDP or other components, the categories are not being sent inside the product
      //  but the thickness, width and length valeus are directly sent, so we calculate the weight based on these values
      if (this.product.categories === undefined) {
        return calcKgEach(this.thickness, this.width, this.length, this.numberOfSheets); // call to the weight-utils.ts method
      }
      const width = Number((this.product.categories?.find(category => category.code?.includes("width"))?.name as string).replaceAll(",", ""));
      const thickness = Number((this.product.categories?.find(category => category.code?.includes("thickness"))?.name as string).replaceAll(",", ""));
      let length: string | number = (this.product.categories?.find(category => category.code?.includes("length"))?.name as string).replaceAll(",", "");
      if(isNaN(Number(length))) {
        length = this.length;
      }
      return calcKgEach(thickness, width, Number(length), this.numberOfSheets);
    }

    private updateComponent(): void {
      if (this.product) {
        this.productCode = this.product.code ?? '';
        this.setStockInfo(this.product);
        this.cd.markForCheck();
      } else if (this.productCode) {
        // force hasStock and quantity for the time being, as we do not have more info:
        this.quantity = 1;
        this.hasStock = true;
        this.cd.markForCheck();
      } else {
        this.subscription = (
          this.productListItemContext
            ? this.productListItemContext.product$
            : this.currentProductService.getProduct()
        )
        .pipe(filter(isNotNullable))
        .subscribe((product) => {
          this.productCode = product.code ?? '';
          this.setStockInfo(product);
          this.cd.markForCheck();
        });
      }
    }

    protected setStockInfo(product: Product): void {
      this.quantity = 1;

      if (product.stock) {
        this.maxQuantity = product.stock.stockLevel ?? 0;
        this.hasStock = this.maxQuantity > 0;
        this.forceInStock = (product.stock.stockLevelStatus?.includes("inStock")) as boolean && this.maxQuantity === 0;
        this.inventoryThreshold = !!product.stock.isValueRounded;
      } else {
        this.maxQuantity = 0;
        this.hasStock = false;
      }

      if (this.productListItemContext) {
        this.showQuantity = false;
      }
    }


    /**
     * In specific scenarios, we need to omit displaying the stock level or append a plus to the value.
     * When backoffice forces a product to be in stock, omit showing the stock level.
     * When product stock level is limited by a threshold value, append '+' at the end.
     * When out of stock, display no numerical value.
     */
    getInventory(): string {
      if (this.hasStock) {
        const quantityDisplay = this.maxQuantity
          ? this.maxQuantity.toString()
          : '';
        return this.inventoryThreshold ? quantityDisplay + '+' : quantityDisplay;
      } else {
        return '';
      }
    }

    addToCart() {
      const quantity = this.customQuantity ? this.customQuantity : this.addToCartForm.get('quantity')?.value;
      if (!this.productCode || quantity <= 0) {
        return;
      }

      if (this.pickupOptionCompRef instanceof ComponentRef) {
        this.pickupOptionCompRef.instance.intendedPickupLocation$
          .pipe(take(1))
          .subscribe((intendedPickupLocation: any) => {
            this.pickupStore =
              intendedPickupLocation?.pickupOption === 'pickup'
                ? intendedPickupLocation.name
                : undefined;
          });
      }

      this.activeCartService
        .getEntries()
        .pipe(take(1))
        .subscribe((cartEntries) => {
          this.activeCartService.gonvarriAddEntry(
            this.productCode,
            quantity,
            this.numberOfSheets,
            this.packageWeight,
            Boolean(this.product.isContract),
            this.pickupStore,
            this.length
          );

          this.eventService.dispatch(
            this.createCartUiEventAddToCart(
              this.productCode,
              quantity,
              cartEntries.length,
              this.pickupStore
            )
          );
        });
    }

    protected createCartUiEventAddToCart(
      productCode: string,
      quantity: number,
      numberOfEntriesBeforeAdd: number,
      storeName?: string
    ) {
      const newEvent = new CartUiEventAddToCart();
      newEvent.productCode = productCode;
      newEvent.quantity = quantity;
      newEvent.numberOfEntriesBeforeAdd = numberOfEntriesBeforeAdd;
      newEvent.pickupStoreName = storeName;
      if (this.featureConfigService.isEnabled('a11yDialogTriggerRefocus')) {
        newEvent.triggerElementRef = this.addToCartDialogTriggerEl;
      }
      return newEvent;
    }
}
